class AnnouncementCountdown extends HTMLElement {
  constructor() {
    super();

    this.interval = null;
    this.deadline = null;
    this.storageKey = '';
    this.segmentElements = new Map();
    this.valueElements = new Map();
    this.visibleUnits = [];
    this.expiredMessage = null;
    this.initialized = false;
    this.onVisibilityChange = this.handleVisibilityChange.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.storageKey = this.dataset.storageKey || `announcement-countdown:${this.dataset.instanceId || 'default'}`;
    this.expiredMessage = this.querySelector('[data-expired-message]');

    Array.from(this.querySelectorAll('[data-segment]')).forEach((segment) => {
      const unit = segment.dataset.segment;
      if (!unit) return;

      this.segmentElements.set(unit, segment);

      const value = segment.querySelector('[data-value]');
      if (value) {
        this.valueElements.set(unit, value);
      }
    });

    this.visibleUnits = ['days', 'hours', 'minutes', 'seconds'].filter((unit) => this.segmentElements.has(unit));

    if (!this.visibleUnits.length) {
      this.hidden = true;
      this.syncSlideVisibility();
      return;
    }

    this.deadline = this.resolveDeadline(Date.now());

    if (!this.isValidTimestamp(this.deadline)) {
      this.hidden = true;
      this.syncSlideVisibility();
      return;
    }

    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.tick();
    this.start();
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  start() {
    if (this.interval || !this.shouldKeepTicking()) return;

    this.interval = window.setInterval(() => {
      this.tick();
    }, 1000);
  }

  stop() {
    if (!this.interval) return;

    window.clearInterval(this.interval);
    this.interval = null;
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.stop();
      return;
    }

    this.tick();
    this.start();
  }

  shouldKeepTicking() {
    return Boolean(
      this.deadline
      || this.dataset.mode === 'daily'
      || (this.dataset.mode === 'evergreen' && this.isTrue(this.dataset.restartEvergreen))
    );
  }

  tick() {
    const now = Date.now();

    if (!this.isValidTimestamp(this.deadline)) {
      this.deadline = this.resolveDeadline(now);
    }

    if (!this.isValidTimestamp(this.deadline)) {
      this.hidden = true;
      this.syncSlideVisibility();
      this.stop();
      return;
    }

    let remainingMs = this.deadline - now;

    if (remainingMs <= 0) {
      remainingMs = this.handleExpiry(now);
    }

    if (remainingMs <= 0 && !this.deadline && this.dataset.expiredState !== 'zero') {
      this.applyExpiredState();
      this.stop();
      return;
    }

    this.applyActiveState();
    this.renderValues(remainingMs);

    if (remainingMs <= 0 && !this.shouldKeepTicking()) {
      this.stop();
    }
  }

  handleExpiry(now) {
    if (this.dataset.mode === 'daily') {
      this.deadline = this.resolveDailyDeadline(now + 1000);
      return Math.max((this.deadline || 0) - now, 0);
    }

    if (this.dataset.mode === 'evergreen' && this.isTrue(this.dataset.restartEvergreen)) {
      this.deadline = this.createEvergreenDeadline(now);
      return Math.max((this.deadline || 0) - now, 0);
    }

    this.deadline = null;

    if (this.dataset.expiredState === 'zero') {
      return 0;
    }

    this.applyExpiredState();
    return 0;
  }

  applyActiveState() {
    this.hidden = false;
    this.syncSlideVisibility();

    const segments = this.querySelector('[data-timer-segments]');
    if (segments) segments.hidden = false;
    if (this.expiredMessage) this.expiredMessage.hidden = true;
  }

  applyExpiredState() {
    const expiredState = this.dataset.expiredState || 'message';
    const segments = this.querySelector('[data-timer-segments]');

    if (expiredState === 'hide') {
      this.hidden = true;
      this.syncSlideVisibility();
      if (segments) segments.hidden = true;
      if (this.expiredMessage) this.expiredMessage.hidden = true;
      return;
    }

    this.hidden = false;
    this.syncSlideVisibility();

    if (expiredState === 'message') {
      if (segments) segments.hidden = true;

      if (this.expiredMessage && this.expiredMessage.textContent.trim() !== '') {
        this.expiredMessage.hidden = false;
      } else {
        this.hidden = true;
      }

      this.syncSlideVisibility();
      return;
    }

    if (segments) segments.hidden = false;
    if (this.expiredMessage) this.expiredMessage.hidden = true;
    this.renderValues(0);
  }

  renderValues(remainingMs) {
    const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
    const unitSeconds = {
      days: 86400,
      hours: 3600,
      minutes: 60,
      seconds: 1,
    };
    const values = {};
    let remainder = totalSeconds;

    this.visibleUnits.forEach((unit, index) => {
      const secondsForUnit = unitSeconds[unit];
      const value = Math.floor(remainder / secondsForUnit);
      values[unit] = value;

      if (index !== this.visibleUnits.length - 1) {
        remainder -= value * secondsForUnit;
      }
    });

    const hideZeroDays =
      this.isTrue(this.dataset.hideZeroDays)
      && this.visibleUnits.includes('days')
      && this.visibleUnits.length > 1
      && (values.days || 0) === 0;

    this.segmentElements.forEach((segment, unit) => {
      if (unit === 'days' && hideZeroDays) {
        segment.hidden = true;
      } else {
        segment.hidden = false;
      }
    });

    this.valueElements.forEach((valueElement, unit) => {
      valueElement.textContent = this.formatValue(values[unit] || 0);
    });
  }

  formatValue(value) {
    if (!this.isTrue(this.dataset.padValues)) {
      return `${value}`;
    }

    return `${value}`.padStart(2, '0');
  }

  resolveDeadline(now) {
    switch (this.dataset.mode) {
      case 'daily':
        return this.resolveDailyDeadline(now);
      case 'evergreen':
        return this.resolveEvergreenDeadline(now);
      default:
        return this.resolveFixedDeadline();
    }
  }

  resolveFixedDeadline() {
    const date = `${this.dataset.fixedDate || ''}`.trim();

    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;

    const time = this.normalizeTime(this.dataset.fixedTime);

    if (this.dataset.timezoneMode === 'browser') {
      const deadline = new Date(`${date}T${time}`);
      return Number.isFinite(deadline.getTime()) ? deadline.getTime() : null;
    }

    const offset = this.resolveOffsetString();
    const deadline = new Date(`${date}T${time}${offset || ''}`);

    return Number.isFinite(deadline.getTime()) ? deadline.getTime() : null;
  }

  resolveDailyDeadline(now) {
    const timeParts = this.parseTimeParts(this.dataset.dailyTime);

    if (this.dataset.timezoneMode === 'browser') {
      const target = new Date(now);
      target.setHours(timeParts.hours, timeParts.minutes, timeParts.seconds, 0);

      if (target.getTime() <= now) {
        target.setDate(target.getDate() + 1);
      }

      return target.getTime();
    }

    const offsetMinutes = this.parseOffsetMinutes(this.resolveOffsetString());
    if (offsetMinutes === null) {
      const fallback = new Date(now);
      fallback.setHours(timeParts.hours, timeParts.minutes, timeParts.seconds, 0);

      if (fallback.getTime() <= now) {
        fallback.setDate(fallback.getDate() + 1);
      }

      return fallback.getTime();
    }

    const adjustedNow = new Date(now + offsetMinutes * 60000);
    let target = Date.UTC(
      adjustedNow.getUTCFullYear(),
      adjustedNow.getUTCMonth(),
      adjustedNow.getUTCDate(),
      timeParts.hours,
      timeParts.minutes,
      timeParts.seconds
    ) - offsetMinutes * 60000;

    if (target <= now) {
      adjustedNow.setUTCDate(adjustedNow.getUTCDate() + 1);
      target = Date.UTC(
        adjustedNow.getUTCFullYear(),
        adjustedNow.getUTCMonth(),
        adjustedNow.getUTCDate(),
        timeParts.hours,
        timeParts.minutes,
        timeParts.seconds
      ) - offsetMinutes * 60000;
    }

    return target;
  }

  resolveEvergreenDeadline(now) {
    const duration = this.getEvergreenDuration();

    if (duration <= 0) return null;

    const storedDeadline = this.readStoredDeadline();

    if (storedDeadline && storedDeadline > now) {
      return storedDeadline;
    }

    if (storedDeadline && storedDeadline <= now && !this.isTrue(this.dataset.restartEvergreen)) {
      return storedDeadline;
    }

    return this.createEvergreenDeadline(now);
  }

  createEvergreenDeadline(now) {
    const duration = this.getEvergreenDuration();

    if (duration <= 0) return null;

    const deadline = now + duration;
    this.writeStoredDeadline(deadline);

    return deadline;
  }

  getEvergreenDuration() {
    const days = this.parseNumber(this.dataset.evergreenDays);
    const hours = this.parseNumber(this.dataset.evergreenHours);
    const minutes = this.parseNumber(this.dataset.evergreenMinutes);
    const seconds = this.parseNumber(this.dataset.evergreenSeconds);

    return (((days * 24 + hours) * 60 + minutes) * 60 + seconds) * 1000;
  }

  readStoredDeadline() {
    try {
      const stored = window.localStorage.getItem(this.storageKey);
      const value = Number.parseInt(`${stored}`, 10);
      return Number.isFinite(value) ? value : null;
    } catch (error) {
      return null;
    }
  }

  writeStoredDeadline(deadline) {
    try {
      window.localStorage.setItem(this.storageKey, `${deadline}`);
    } catch (error) {
      /* no-op */
    }
  }

  resolveOffsetString() {
    const mode = this.dataset.timezoneMode || 'store';

    if (mode === 'browser') return '';
    if (mode === 'custom') return this.normalizeOffset(this.dataset.timezoneOffset);

    return this.normalizeOffset(this.dataset.storeOffset || this.dataset.timezoneOffset);
  }

  normalizeOffset(value) {
    const input = `${value || ''}`.trim().toUpperCase();

    if (!input) return '';
    if (input === 'UTC' || input === 'Z' || input === '+00:00' || input === '+0000') return 'Z';

    const match = input.match(/^([+-])(\d{2}):?(\d{2})$/);
    if (!match) return '';

    return `${match[1]}${match[2]}:${match[3]}`;
  }

  parseOffsetMinutes(value) {
    const normalized = this.normalizeOffset(value);

    if (!normalized) return null;
    if (normalized === 'Z') return 0;

    const match = normalized.match(/^([+-])(\d{2}):(\d{2})$/);
    if (!match) return null;

    const sign = match[1] === '-' ? -1 : 1;
    return sign * (Number.parseInt(match[2], 10) * 60 + Number.parseInt(match[3], 10));
  }

  parseTimeParts(value) {
    const parts = `${value || ''}`
      .trim()
      .split(':')
      .map((part) => Number.parseInt(part, 10));

    return {
      hours: this.clampTimePart(parts[0], 23),
      minutes: this.clampTimePart(parts[1], 59),
      seconds: this.clampTimePart(parts[2], 59),
    };
  }

  normalizeTime(value) {
    const parts = this.parseTimeParts(value);
    return `${`${parts.hours}`.padStart(2, '0')}:${`${parts.minutes}`.padStart(2, '0')}:${`${parts.seconds}`.padStart(2, '0')}`;
  }

  clampTimePart(value, max) {
    if (!Number.isFinite(value)) return 0;
    if (value < 0) return 0;
    if (value > max) return max;
    return value;
  }

  parseNumber(value) {
    const parsed = Number.parseInt(`${value || '0'}`, 10);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  isTrue(value) {
    return `${value}` === 'true';
  }

  isValidTimestamp(value) {
    return Number.isFinite(value);
  }

  syncSlideVisibility() {
    const slide = this.closest('[data-announcement-slide], slideshow-slide');
    if (slide) {
      slide.hidden = this.hidden;
    }
  }
}

if (!customElements.get('announcement-countdown')) {
  customElements.define('announcement-countdown', AnnouncementCountdown);
}
